import { Chip } from "matsuri-ui"
import { css } from "@emotion/react"
import { Link } from "react-router-dom"
import { use, useState, useRef, useEffect } from "react"
import { theme } from "../theme"

interface SearchOption {
    label: string
    url: string
    tags: string[]
}

interface QuickSearchResultsProps {
    searchPromise: Promise<SearchOption[]>
    onClose: () => void
    onNavigate: (url: string) => void
}

export const QuickSearchResults = ({
    searchPromise,
    onClose,
    onNavigate
}: QuickSearchResultsProps) => {
    const options = use(searchPromise)
    const [selected, setSelected] = useState(0)
    const optionRefs = useRef<(HTMLAnchorElement | null)[]>([])

    const selectOption = (index: number) => {
        setSelected(index)
        optionRefs.current[index]?.focus()
    }

    useEffect(() => {
        const handler = (event: KeyboardEvent) => {
            if (event.key === "ArrowUp") {
                event.preventDefault()
                selectOption(Math.max(selected - 1, 0))
            }
            if (event.key === "ArrowDown") {
                event.preventDefault()
                selectOption(Math.min(selected + 1, options.length - 1))
            }
            if (event.key === "Enter") {
                event.preventDefault()
                if (options[selected]) {
                    onNavigate(options[selected].url)
                }
            }
        }

        window.addEventListener("keydown", handler)
        return () => {
            window.removeEventListener("keydown", handler)
        }
    }, [selected, options.length, onNavigate, options])

    return (
        <div>
            {options?.map((option, index) => (
                <Link
                    to={option.url}
                    key={option.url}
                    ref={element => {
                        optionRefs.current[index] = element
                    }}
                    aria-selected={selected === index}
                    css={[
                        theme.typography.body,
                        css`
                            display: flex;
                            gap: 8px;
                            align-items: flex-start;
                            padding: 16px 16px;
                            font-size: 18px;
                            color: inherit;
                            text-decoration: none;

                            &[aria-selected="true"] {
                                background-color: ${theme.palette.gray[100]};
                            }

                            :hover {
                                background-color: ${theme.palette.gray[200]};
                            }
                        `
                    ]}
                    onClick={onClose}
                >
                    {option.tags.map((tag, index) => (
                        <Chip key={index}>{tag}</Chip>
                    ))}
                    {option.label}
                </Link>
            ))}
        </div>
    )
}
